import { cx } from '@linaria/core';
import { useState, useEffect, useMemo, createContext, Suspense, useContext, useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2ConfigStore } from '@pm2/store';
import { useUserIdentityState } from '@provider/user-identity';
import { CSSRefTransition } from '@utils/react';
import { toolListCls, TOOL_LIST_APPEAR_DURATION_MS, toolCls } from './root.tools.style.js';
import { CourseBuilder } from './tool.course-builder.js';
import { LogIn } from './tool.log-in.js';
import { Search } from './tool.search.js';
import { UserAreaTool } from './tool.user-area.js';
import { ToolKinds } from './typing.tool-kinds.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Toolbox } from './Toolbox/root.toolbox.js';

// use a context to propagate enable / disable methods to the RenderTool utility
const ToolsContext = /*#__PURE__*/createContext({
  enabledTool: undefined,
  onEnableTool: () => {},
  // eslint-disable-line @typescript-eslint/no-empty-function
  onDisableTool: () => {} // eslint-disable-line @typescript-eslint/no-empty-function
});
/**
 * list of available top-level tools on the portal
 *
 * keeps track of currently active tool internally, making sure that no more
 * than one tool can be active at any given time.
 */
function Tools({
  toolsRef,
  onMount
}) {
  const user = useUserIdentityState(state => state.user);
  const publisher = pm2ConfigStore.useSelector(state => state.config.publisher);
  const [enabledTool, setEnabledTool] = useState(undefined);
  useEffect(() => {
    onMount?.();
    if (new URLSearchParams(window.location.search).has("dashboard")) {
      setTimeout(() => {
        setEnabledTool(ToolKinds.USER_AREA);
      }, 500);
    }
  }, [onMount]);
  const context = useMemo(() => ({
    enabledTool,
    onEnableTool: kind => {
      setEnabledTool(kind);
    },
    onDisableTool: tool => {
      setEnabledTool(prevTool => prevTool === tool ? undefined : prevTool);
    }
  }), [enabledTool]);
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    appear: true,
    children: /*#__PURE__*/jsx(CSSRefTransition, {
      timeout: TOOL_LIST_APPEAR_DURATION_MS,
      classNames: toolListCls,
      nodeRef: toolsRef,
      children: ref => /*#__PURE__*/jsx(ToolsContext.Provider, {
        value: context,
        children: /*#__PURE__*/jsxs("ul", {
          ref: ref,
          className: cx(toolListCls, enabledTool !== undefined && "toolEnabled"),
          children: [/*#__PURE__*/jsx(RenderTool, {
            kind: ToolKinds.SEARCH
          }), /*#__PURE__*/jsx(Suspense, {
            fallback: null,
            children: /*#__PURE__*/jsx(RenderTool, {
              kind: ToolKinds.COURSE_PICKER
            })
          }), /*#__PURE__*/jsx(RenderTool, {
            kind: ToolKinds.TOOLBOX
          }), publisher !== "louisiana" && (user ? /*#__PURE__*/jsx(RenderTool, {
            kind: ToolKinds.USER_AREA
          }) : /*#__PURE__*/jsx(RenderTool, {
            kind: ToolKinds.LOG_IN
          }))]
        })
      })
    })
  });
}

// render tool based on it's kind
const RenderTool = ({
  kind
}) => {
  const Tool = getToolComponentByKind(kind);
  const {
    enabledTool,
    onEnableTool,
    onDisableTool
  } = useContext(ToolsContext);
  const handleEnable = useCallback(() => onEnableTool(kind), [onEnableTool, kind]);
  const handleDisable = useCallback(() => onDisableTool(kind), [onDisableTool, kind]);
  return /*#__PURE__*/jsx("li", {
    className: cx(toolCls, enabledTool === kind && "enabled"),
    children: /*#__PURE__*/jsx(Tool, {
      enabled: enabledTool === kind,
      onEnable: handleEnable,
      onDisable: handleDisable
    })
  });
};
function getToolComponentByKind(kind) {
  switch (kind) {
    case ToolKinds.TOOLBOX:
      return Toolbox;
    case ToolKinds.COURSE_PICKER:
      return CourseBuilder;
    case ToolKinds.SEARCH:
      return Search;
    case ToolKinds.USER_AREA:
      return UserAreaTool;
    case ToolKinds.LOG_IN:
      return LogIn;
  }
}

export { Tools };
//# sourceMappingURL=root.tools.js.map
